import {
  AssessmentQuestionType,
  DeprecatedActionDue,
  UID,
  UUID,
} from 'types/types';
import {
  EPFieldSettingsFieldCreate,
  EPFieldSettingsFieldUpdate,
  EPFieldSettingsGet,
  EPFieldSettingsGroupCreate,
  EPFieldSettingsGroupUpdate,
  EPFieldSettingsResultCount,
  EPFieldSettingsSectionCreate,
  EPFieldSettingsSectionUpdate,
  EPFieldSettingsUpdate,
} from 'types/Endpoints/FieldSettings';
import { Action, createAction, createAsyncAction } from 'typesafe-actions';

import {
  EPBillingAdminOrganisationSave,
  EPBillingData,
  EPBillingGet,
} from 'types/Endpoints/Billing';
import { ReportData, ReportEnum, ReportTypes } from 'types/Reports';
import { ApiUser, ApiWebhook } from 'types/Api';
import {
  Engage,
  EngageDocument,
  EngageElement,
  EngageElementCheck,
} from 'types/EngageSettings';
import { BillingDiscount, BillingItem, BillingProduct } from 'types/Billing';
import { SsoOrganisation } from 'types/SsoSettings';
import { EPOrganisationSettingsSave } from 'types/Endpoints/Organisation';
import { AseSetupFlag, AseSetupSignatory } from 'types/Assessments';
import { FileBeingAdded } from 'service/file/types';
import {
  APIRequestCreateAsset,
  AssessmentFlag,
  AssessmentLibrary,
  AssessmentLibraryOpened,
  AssessmentTemplateOpened,
  AssessmentPdf,
  AssessmentScheme,
  AssessmentSettings,
  AssessmentSuggestions,
  AssessmentTemplate,
  AssessmentValidityTimescale,
  Categories,
  CategoryGroups,
  ComplianceSubtype,
  ComplianceType,
  DueTypes,
  OrganisationBasic,
  Severities,
  Timescales,
  FieldSection,
  FieldGroup,
  FieldField,
  AssessmentTemplateDefaultCost,
  FieldGroupCheckType,
  AssessmentCheck,
  ReportPage,
  SetupOrganisation,
  AssessmentSignatory,
  RemoteState,
} from './types';

export const DO_OFFLINE_CHECK = createAction('@REMOTE/DO_OFFLINE_CHECK')();

export const REMOTE_LOAD_CREATE_ASSET = createAction(
  '@REMOTE/LOAD_CREATE_ASSET',
)();
export const REMOTE_CREATE_ASSET = createAction(
  '@REMOTE/CREATE_ASSET',
)<APIRequestCreateAsset>();

/** Billing
 */

export const doSetupBillingOpen = createAction('@doSetupBillingOpen')<{
  organisationId: UID;
}>();
export const doSetupBillingSetData = createAction('@doSetupBillingSetData')<{
  addToUsage: boolean;
  details: EPBillingGet['response']['details'];
  draftInvoices: EPBillingGet['response']['draftInvoices'];
  invoices: EPBillingGet['response']['invoices'];
  statement: EPBillingGet['response']['statement'];
}>();
export const doSetupBillingClosed = createAction('@doSetupBillingClosed')();

export const doSetupBillingPdf = createAction('@doSetupBillingPdf')<{
  organisationId: UID;
  reference: string;
  doShare: boolean;
}>();
export const doSetupBillingCreditNote = createAction(
  '@doSetupBillingCreditNote',
)<{
  organisationId: UID;
  reference: string;
  doShare: boolean;
}>();
export const doSetupBillingStatementPdf = createAction(
  '@doSetupBillingStatementPdf',
)<{
  organisationId: UID;
  doShare: boolean;
}>();
export const doSetupBillingStatementCsv = createAction(
  '@doSetupBillingStatementCsv',
)<{
  organisationId: UID;
}>();
export const doSetupBillingCsv = createAction('@doSetupBillingCsv')<{
  organisationId: UID;
  reference: string;
}>();
export const doSetupBillingDraftPdf = createAction('@doSetupBillingDraftPdf')<{
  organisationId: UID;
  code: string;
  date: number;
  doShare: boolean;
}>();
export const doSetupBillingDraftCsv = createAction('@doSetupBillingDraftCsv')<{
  organisationId: UID;
  code: string;
  date: number;
}>();

/**
 * Billing Admin
 */

export const doSetupBillingAdminOpen = createAction(
  '@doSetupBillingAdminOpen',
)<{
  organisationId: UID;
}>();
export const doSetupBillingAdminClosed = createAction(
  '@doSetupBillingAdminClosed',
)();
export const doSetupBillingAdminSetData = createAction(
  '@doSetupBillingAdminSetData',
)<{
  addToUsage: boolean;
  organisation?: EPBillingData['response']['organisation'];
  products?: EPBillingData['response']['products'];
  productUnits?: EPBillingData['response']['productUnits'];
  items?: EPBillingData['response']['items'];
  discounts?: EPBillingData['response']['discounts'];
  customers?: EPBillingData['response']['customers'];
}>();

export const doSetupBillingAdminUpdateProduct = createAction(
  '@doSetupBillingAdminUpdateProduct',
)<BillingProduct>();
export const doSetupBillingAdminDeleteProduct = createAction(
  '@doSetupBillingAdminDeleteProduct',
)<{ id: UID }>();
export const doSetupBillingAdminUpdateProductUnits = createAction(
  '@doSetupBillingAdminUpdateProductUnits',
)<{ productId: UID; units: number }>();
export const doSetupBillingAdminDeleteProductUnits = createAction(
  '@doSetupBillingAdminDeleteProductUnits',
)<{ productId: UID }>();
export const doSetupBillingAdminUpdateItem = createAction(
  '@doSetupBillingAdminUpdateItem',
)<BillingItem>();
export const doSetupBillingAdminDeleteItem = createAction(
  '@doSetupBillingAdminDeleteItem',
)<{ id: UID }>();
export const doSetupBillingAdminUpdateDiscount = createAction(
  '@doSetupBillingAdminUpdateDiscount',
)<BillingDiscount>();
export const doSetupBillingAdminDeleteDiscount = createAction(
  '@doSetupBillingAdminDeleteDiscount',
)<{ id: UID }>();

// Field Settings

export const doFieldSettingsLoad = createAsyncAction(
  '@REMOTE.doFieldSettingsLoad.REQUEST',
  '@REMOTE.doFieldSettingsLoad.RESPONSE',
  '@REMOTE.doFieldSettingsLoad.FAILURE',
)<
  {
    id: string;
  },
  {
    fieldSettings: EPFieldSettingsGet['response'] & {
      newId?: string;
    };
  },
  Error
>();

export const doFieldSettingsLoadResultCount = createAction(
  '@REMOTE.doFieldSettingsLoadResultCount',
)();

export const doFieldSettingsInit = createAction(
  '@REMOTE.doFieldSettingsInit',
)();

export const doFieldSettingUnload = createAction(
  '@REMOTE.doFieldSettingUnload',
)();

export const doFieldSettingsSetData = createAction(
  '@REMOTE.doFieldSettingsSetData',
)<EPFieldSettingsGet['response']>();

export const doFieldSettingsSetResultCount = createAction(
  '@REMOTE.doFieldSettingsSetResultCount',
)<EPFieldSettingsResultCount['response']['fields'] | undefined>();

export const doFieldSettingsSetSectionId = createAction(
  '@REMOTE.doFieldSettingsSetSectionId',
)<string | undefined>();

export const doFieldSettingsSetGroupId = createAction(
  '@REMOTE.doFieldSettingsSetGroupId',
)<string | undefined>();

export const doFieldSettingsSetFieldId = createAction(
  '@REMOTE.doFieldSettingsSetFieldId',
)<string | undefined>();

export const doFieldSettingsSetLiteGroupId = createAction(
  '@REMOTE.doFieldSettingsSetLiteGroupId',
)<string | undefined>();

export const doFieldSettingsReorderSections = createAction(
  '@REMOTE.doFieldSettingsReorderSections',
)();

export const doFieldSettingsSave = createAction('@REMOTE.doFieldSettingsSave')<{
  fireActionAfter?: Action;
  data: EPFieldSettingsUpdate['post'];
}>();

export const doFieldSettingsSectionSave = createAction(
  '@REMOTE.doFieldSettingsSectionSave',
)<
  | {
      id?: never;
      data: EPFieldSettingsSectionCreate['post'];
    }
  | {
      id: string;
      data: EPFieldSettingsSectionUpdate['post'];
    }
>();

export const doFieldSettingsSectionSaved = createAction(
  '@REMOTE.doFieldSettingsSectionSaved',
)<string>();

export const doFieldSettingsSectionDelete = createAction(
  '@REMOTE.doFieldSettingsSectionDelete',
)<{ id: string }>();

export const doFieldSettingsLinkCustomer = createAction(
  '@REMOTE.doFieldSettingsLinkCustomer',
)<{ clientIds: string[]; id: string }>();

export const doFieldSettingsDeleteCustomer = createAction(
  '@REMOTE.doFieldSettingsDeleteCustomer',
)<{ clientId: string; id: string }>();

export const doFieldSettingUnlinkCustomer = createAction(
  '@REMOTE.doFieldSettingUnlinkCustomer',
)<{ clientId: string; id: string }>();

export const doFieldSettingsGroupSave = createAction(
  '@REMOTE.doFieldSettingsGroupSave',
)<
  | {
      id?: never;
      isLite: boolean;
      data: EPFieldSettingsGroupCreate['post'];
    }
  | {
      id: string;
      isLite: boolean;
      data: EPFieldSettingsGroupUpdate['post'];
    }
>();

export const doFieldSettingsGroupSaved = createAction(
  '@REMOTE.doFieldSettingsGroupSaved',
)<{ id: string; isLite: boolean }>();

export const doFieldSettingsGroupAddGroup = createAction(
  '@doFieldSettingsGroupAddGroup',
)<{ id: string; childGroupId: string }>();

export const doFieldSettingsGroupDelete = createAction(
  '@REMOTE.doFieldSettingsGroupDelete',
)<{ id: string }>();

export const doFieldSettingsFieldSave = createAction(
  '@REMOTE.doFieldSettingsFieldSave',
)<
  | {
      id?: never;
      data: EPFieldSettingsFieldCreate['post'];
    }
  | {
      id: string;
      data: EPFieldSettingsFieldUpdate['post'];
    }
>();

export const doFieldSettingsFieldSaved = createAction(
  '@REMOTE.doFieldSettingsFieldSaved',
)<string>();

export const doFieldSettingsFieldDelete = createAction(
  '@REMOTE.doFieldSettingsFieldDelete',
)<{ id: string }>();

// User Profile

export const REMOTE_ACCOUNT_SAVE_AVATAR = createAction(
  '@REMOTE/REMOTE_ACCOUNT_SAVE_AVATAR',
)<{
  image: string | null;
}>();

export const REMOTE_ACCOUNT_LOAD_DETAILS = createAction(
  '@REMOTE/REMOTE_ACCOUNT_LOAD_DETAILS',
)();

export const REMOTE_ACCOUNT_SAVE_DETAILS = createAction(
  '@REMOTE/REMOTE_ACCOUNT_SAVE_DETAILS',
)<{
  forename: string;
  surname: string;
  phone: string;
  postNominals: string;
  qualifications: string;
}>();

export const REMOTE_ACCOUNT_LOAD_TFA = createAction(
  '@REMOTE/REMOTE_ACCOUNT_LOAD_TFA',
)();

export const REMOTE_ACCOUNT_SAVE_TFA = createAction(
  '@REMOTE/REMOTE_ACCOUNT_SAVE_TFA',
)<{ token: string } | undefined>();

export const REMOTE_ACCOUNT_EMAIL_CHANGE_LOAD = createAction(
  '@REMOTE/REMOTE_ACCOUNT_EMAIL_CHANGE_LOAD',
)();

export const REMOTE_ACCOUNT_EMAIL_CHANGE_REQUEST = createAction(
  '@REMOTE/REMOTE_ACCOUNT_EMAIL_CHANGE_REQUEST',
)<{ email: string }>();

export const REMOTE_ACCOUNT_EMAIL_CHANGE_CONFIRM = createAction(
  '@REMOTE/UPDATE_USER_EMAIL',
)<{
  token: string;
  code: string;
}>();

export const REMOTE_ACCOUNT_PASSWORD_CHANGE_LOAD = createAction(
  '@REMOTE/REMOTE_ACCOUNT_PASSWORD_CHANGE_LOAD',
)();
export const REMOTE_ACCOUNT_PASSWORD_CHANGE_SAVE = createAction(
  '@REMOTE/REMOTE_ACCOUNT_PASSWORD_CHANGE_SAVE',
)<{ oldPassword: string; newPassword: string }>();
export const REMOTE_ACCOUNT_PASSWORD_DELETE = createAction(
  '@REMOTE/REMOTE_ACCOUNT_PASSWORD_DELETE',
)();

// Setup

export const doRemoteSetOrganisation = createAction('@doRemoteSetOrganisation')<
  string | undefined
>();

// Assessment Compliance

export const doSettingsAseChecksLoad = createAction(
  '@doSettingsAseChecksLoad',
)<{
  organisationId: string;
}>();

export const doSettingsAseChecksSetData = createAction(
  '@doSettingsAseChecksSetData',
)<{
  checks: Record<string, AssessmentCheck>;
  complianceTypes: Record<string, ComplianceType>;
  complianceSubtypes: Record<string, ComplianceSubtype>;
}>();

export const doSettingsAseChecksMergeData = createAction(
  '@doSettingsAseChecksMergeData',
)<
  Partial<{
    checks: Record<string, AssessmentCheck>;
    complianceTypes: Record<string, ComplianceType>;
    complianceSubtypes: Record<string, ComplianceSubtype>;
  }>
>();

export const doSettingsAseCheckOpen = createAction('@doSettingsAseCheckOpen')<{
  id: string;
}>();

export const doSettingsAseCheckSave = createAction('@doSettingsAseCheckSave')<{
  id: string;
  assessmentTypeId: number | null;
}>();

// Assessment Setup Template

export const doSetupAseLoad = createAction('@doSetupAseLoad')<{
  organisationId: string;
}>();

export const doSetupAseSetData = createAction('@doSetupAseSetData')<{
  templates: Record<string, AssessmentTemplate>;
  templateDefaultCosts: AssessmentTemplateDefaultCost;
  complianceTypes: Record<string, ComplianceType>;
  complianceSubtypes: Record<string, ComplianceSubtype>;
  schemes: Record<string, AssessmentScheme>;
  pdfs: Record<string, AssessmentPdf>;
  flags: Record<string, AssessmentFlag>;
  signatories: Record<string, AssessmentSignatory>;
  validityTimescales: Record<string, AssessmentValidityTimescale>;
  organisation: OrganisationBasic;
  customers: Record<string, OrganisationBasic>;
  library: Pick<
    AssessmentLibrary,
    'folders' | 'sections' | 'questions' | 'actionDefinitions'
  >;
  fieldSectionIdsByOrganisation: Record<UUID, number[]>;
  fieldSections: Record<string, FieldSection>;
  fieldGroups: Record<string, FieldGroup>;
  fieldFields: Record<string, FieldField>;
  fieldGroupCheckTypes: Record<string, FieldGroupCheckType>;
}>();

export const doSetupAseMergeData = createAction('@doSetupAseMergeData')<{
  opened?: Partial<AssessmentTemplateOpened>;
  templates?: Record<string, AssessmentTemplate>;
  flags?: Record<string, AssessmentFlag>;
  signatories?: Record<string, AssessmentSignatory>;
  library?: {
    questions: AssessmentLibrary['questions'] | undefined;
  };
}>();

export const doSetupAseRemoveData = createAction('@doSetupAseRemove')<{
  templates?: number[];
}>();

export const doSettingPageClose = createAction('@doSettingPageClose')();

export const doSetupAseClosed = createAction('@doSetupAseClosed')();

export const doAseSetupTemplateOpen = createAction(
  '@REMOTE.doAseSetupTemplateOpen',
)<{
  id: number | undefined;
  newData?: {
    complianceSubtypeId: number;
  };
}>();

export const doAseSetupTemplateSave = createAction(
  '@REMOTE.doAseSetupTemplateSave',
)<{
  id: number | null;
  data: AssessmentTemplate & { questionIds: number[] };
  close: boolean;
}>();

export const doAseSetupTemplateDelete = createAction(
  '@REMOTE.doAseSetupTemplateDelete',
)<{
  id: number;
}>();

export const doAseSetupTemplateClosed = createAction(
  '@REMOTE.doAseSetupTemplateClosed',
)();

export const doAseSetupFlagSave = createAction('@REMOTE.doAseSetupFlagSave')<{
  flag: AseSetupFlag;
  isNew: boolean;
}>();

export const doAseSetupFlagDelete = createAction(
  '@REMOTE.doAseSetupFlagDelete',
)<{
  flagId: UID;
  name: string;
}>();

export const doAseSetupSignatorySave = createAction('doAseSetupSignatorySave')<{
  signatory: AseSetupSignatory;
  isNew: boolean;
}>();

export const doAseSetupSignatoriesOrder = createAction(
  'doAseSetupSignatoriesOrder',
)<{
  order: UID[];
}>();

export const doAseSetupSignatoryDelete = createAction(
  'doAseSetupSignatoryDelete',
)<{
  id: UID;
  name: string;
}>();

// Assessment Setup Library

export const REMOTE_ASESETUP_LOAD = createAction('@REMOTE_ASESETUP_LOAD')<{
  organisationId: string;
}>();

export const REMOTE_ASESETUP_SET = createAction('@REMOTE_ASESETUP_SET')<{
  categories: Categories;
  categoryGroups: CategoryGroups;
  severities: Severities;
  timescales: Timescales;
  dueTypes: DueTypes;
  suggestions: AssessmentSuggestions;
  library: AssessmentLibrary;
}>();

export const REMOTE_ASESETUP_MERGE = createAction('@REMOTE_ASESETUP_MERGE')<{
  opened?: Partial<AssessmentLibraryOpened>;
  settings?: Partial<AssessmentSettings>;
  suggestions?: Partial<AssessmentSuggestions>;
  library?: Partial<AssessmentLibrary>;
}>();

export const REMOTE_ASESETUP_REMOVE = createAction('@REMOTE_ASESETUP_REMOVE')<{
  suggestions?: Partial<{
    [P in keyof AssessmentSuggestions]: number[];
  }>;
  library?: Partial<{
    [P in keyof AssessmentLibrary]: number[];
  }>;
}>();

export const REMOTE_ASESETUP_CLOSED = createAction('@REMOTE_ASESETUP_CLOSED')();

export const REMOTE_ASESETUP_COLLAPSEFOLDERSECTIONS = createAction(
  '@REMOTE_ASESETUP_COLLAPSEFOLDERSECTIONS',
)<{ folderId: number; collapse: boolean }>();

export const REMOTE_ASESETUP_TOGGLESECTIONCOLLAPSED = createAction(
  '@REMOTE_ASESETUP_TOGGLESECTIONCOLLAPSED',
)<number>();

// AseSetup Open
export const REMOTE_ASESETUP_FOLDEROPEN = createAction(
  '@REMOTE_ASESETUP_FOLDEROPEN',
)<{
  id: number | undefined;
}>();

export const REMOTE_ASESETUP_SECTIONOPEN = createAction(
  '@REMOTE_ASESETUP_SECTIONOPEN',
)<
  | {
      id?: never;
      folderId: number;
    }
  | {
      id: number;
      folderId?: never;
    }
>();

export const REMOTE_ASESETUPLIBRARY_QUESTIONOPEN = createAction(
  '@REMOTE_ASESETUPLIBRARY_QUESTIONOPEN',
)<
  | {
      id?: never;
      sectionId: number;
      parentQuestionId?: never;
      type: AssessmentQuestionType;
    }
  | {
      id?: never;
      sectionId?: never;
      parentQuestionId: number;
      type: AssessmentQuestionType;
    }
  | {
      id: number;
      sectionId?: never;
      parentQuestionId?: never;
      type?: never;
    }
>();
export const REMOTE_ASESETUP_ANSWEROPEN = createAction(
  '@REMOTE_ASESETUP_ANSWEROPEN',
)<
  | {
      id?: never;
      questionId: number;
    }
  | {
      id: number;
      questionId?: never;
    }
>();
export const REMOTE_ASESETUP_CHECKLISTITEMOPEN = createAction(
  '@REMOTE_ASESETUP_CHECKLISTITEMOPEN',
)<
  | {
      id?: never;
      questionId: number;
    }
  | {
      id: number;
      questionId?: never;
    }
>();
export const REMOTE_ASESETUP_ACTIONOPEN = createAction(
  '@REMOTE_ASESETUP_ACTIONOPEN',
)<{
  id: number | undefined;
  incomingValueNewIdKey: string | undefined;
  allowLinkManagement: boolean;
}>();
export const REMOTE_ASESETUP_CONTROLOPEN = createAction(
  '@REMOTE_ASESETUP_CONTROLOPEN',
)<{
  id: number | undefined;
  incomingValueNewIdKey: string | undefined;
  allowLinkManagement: boolean;
}>();

// AseSetup Order
export const REMOTE_ASESETUP_OPENFOLDERSORDER = createAction(
  '@REMOTE_ASESETUP_OPENFOLDERSORDER',
)();
export const REMOTE_ASESETUP_FOLDERSORDER = createAction(
  '@REMOTE_ASESETUP_FOLDERSORDER',
)<{
  order: number[];
}>();

export const REMOTE_ASESETUP_OPENSECTIONSORDER = createAction(
  '@REMOTE_ASESETUP_OPENSECTIONSORDER',
)<{
  folderId: number;
}>();
export const REMOTE_ASESETUP_SECTIONSORDER = createAction(
  '@REMOTE_ASESETUP_SECTIONSORDER',
)<{
  folderId: number;
  order: number[];
}>();

export const REMOTE_ASESETUP_OPENQUESTIONSORDER = createAction(
  '@REMOTE_ASESETUP_OPENQUESTIONSORDER',
)<
  | {
      sectionId: number;
      answerId?: never;
    }
  | {
      sectionId?: never;
      answerId: number;
    }
>();
export const REMOTE_ASESETUP_QUESTIONSORDER = createAction(
  '@REMOTE_ASESETUP_QUESTIONSORDER',
)<
  (
    | {
        sectionId: number;
        answerId?: never;
      }
    | {
        sectionId?: never;
        answerId: number;
      }
  ) & {
    order: number[];
  }
>();

// AseSetup Save
export const REMOTE_ASESETUP_FOLDERSAVE = createAction(
  '@REMOTE_ASESETUP_FOLDERSAVE',
)<{
  id: number | undefined;
  data: {
    name: string;
  };
}>();
export const REMOTE_ASESETUP_SECTIONSAVE = createAction(
  '@REMOTE_ASESETUP_SECTIONSAVE',
)<{
  id: number | undefined;
  data: {
    name: string;
    description: string;
    reportDescription: string;
  };
  addData: {
    folderId?: number;
  };
}>();
export const REMOTE_ASESETUP_QUESTIONSAVE = createAction(
  '@REMOTE_ASESETUP_QUESTIONSAVE',
)<{
  id: number | undefined;
  data: { question: string; description: string; reportDescription: string };
  controlIds: number[];
  orderedChecklistItemIds: number[];
  orderedAnswerIds: number[];
  linkedQuestionIds: number[];
  addData: {
    sectionId: number | null;
    answerId: number | null;
    type: AssessmentQuestionType;
  };
  close: boolean;
}>();
export const REMOTE_ASESETUP_ANSWERSAVE = createAction(
  '@REMOTE_ASESETUP_ANSWERSAVE',
)<{
  id: number | undefined;
  data: {
    answer: string;
    description: string;
    reportDescription: string;
    negative: boolean;
    actionIds: number[];
  };
  addData: {
    questionId: number;
  };
  close: boolean;
}>();

export const REMOTE_ASESETUP_CHECKLISTITEMSAVE = createAction(
  '@REMOTE_ASESETUP_CHECKLISTITEMSAVE',
)<{
  id: number | undefined;
  data: {
    text: string;
    positiveActionIds: number[];
    negativeActionIds: number[];
    positiveControlIds: number[];
    negativeControlIds: number[];
  };
  addData: {
    questionId: number;
  };
  close: boolean;
}>();
export const REMOTE_ASESETUP_ACTIONSAVE = createAction(
  '@REMOTE_ASESETUP_ACTIONSAVE',
)<{
  id: number | undefined;
  data: {
    what: string;
    why: string;
    categoryId: number;
    severityId: number;
    timescaleId: number;
    actionDefinitionId: string | null;
    cost: string;
    answerIds: number[];
    positiveChecklistItemIds: number[];
    negativeChecklistItemIds: number[];
    due: DeprecatedActionDue;
  };
  incomingValueNewIdKey: string | undefined;
}>();
export const REMOTE_ASESETUP_CONTROLSAVE = createAction(
  '@REMOTE_ASESETUP_CONTROLSAVE',
)<{
  id: number | undefined;
  data: {
    what: string;
    why: string;
    categoryId: number;
    questionIds: number[];
    positiveChecklistItemIds: number[];
    negativeChecklistItemIds: number[];
  };
  incomingValueNewIdKey: string | undefined;
}>();

export const REMOTE_ASESETUP_FOLDERDELETE = createAction(
  '@REMOTE_ASESETUP_FOLDERDELETE',
)<{ id: number }>();
export const REMOTE_ASESETUP_SECTIONDELETE = createAction(
  '@REMOTE_ASESETUP_SECTIONDELETE',
)<{ id: number }>();
export const REMOTE_ASESETUP_QUESTIONDELETE = createAction(
  '@REMOTE_ASESETUP_QUESTIONDELETE',
)<{ id: number }>();
export const REMOTE_ASESETUP_ANSWERDELETE = createAction(
  '@REMOTE_ASESETUP_ANSWERDELETE',
)<{ id: number }>();
export const REMOTE_ASESETUP_CHECKLISTITEMDELETE = createAction(
  '@REMOTE_ASESETUP_CHECKLISTITEMDELETE',
)<{ id: number }>();
export const REMOTE_ASESETUP_ACTIONDELETE = createAction(
  '@REMOTE_ASESETUP_ACTIONDELETE',
)<{
  id: number;
}>();
export const REMOTE_ASESETUP_CONTROLDELETE = createAction(
  '@REMOTE_ASESETUP_CONTROLDELETE',
)<{
  id: number;
}>();

export const REMOTE_ASESETUP_SECTIONCLOSED = createAction(
  '@REMOTE_ASESETUP_SECTIONCLOSED',
)();
export const REMOTE_ASESETUP_QUESTIONCLOSED = createAction(
  '@REMOTE_ASESETUP_QUESTIONCLOSED',
)();
export const REMOTE_ASESETUP_ANSWERCLOSED = createAction(
  '@REMOTE_ASESETUP_ANSWERCLOSED',
)();
export const REMOTE_ASESETUP_CHECKLISTITEMCLOSED = createAction(
  '@REMOTE_ASESETUP_CHECKLISTITEMCLOSED',
)();
export const REMOTE_ASESETUP_ACTIONCLOSED = createAction(
  '@REMOTE_ASESETUP_ACTIONCLOSED',
)();
export const REMOTE_ASESETUP_CONTROLCLOSED = createAction(
  '@REMOTE_ASESETUP_CONTROLCLOSED',
)();

export const doAseSetupLibraryQuestionLinksChosen = createAction(
  'doAseSetupLibraryQuestionLinksChosen',
)<number[]>();

/**
 * Reporting
 */

export const doToggleIsSidebarVisible = createAction(
  '@doToggleIsSidebarVisible',
)();
export const doSetIsSidebarVisible = createAction(
  '@doSetIsSidebarVisible',
)<boolean>();

export const doReportingLoad = createAction('@doReportingLoad')();

export const doReportingSetPages = createAction('@doReportingSetPages')<{
  pages: RemoteState['reporting']['pages'];
}>();

export const doReportingSetLoadingStatus = createAction(
  '@doReportingSetLoadingStatus',
)<RemoteState['reporting']['loadStatus']>();

export const doReportingSelectReportOpen = createAction(
  '@doReportingSelectReportOpen',
)();

export const doReportingSelectReport = createAction(
  '@doReportingSelectReport',
)<{
  id: string;
  filter: Partial<ReportData['filterValues']>;
  key: string | null;
  loadData: boolean;
}>();

export const doReportingAddPage = createAction(
  '@doReportingAddPage',
)<ReportPage>();

export const doReportingOpenPage = createAction('@doReportingOpenPage')<{
  key: string;
}>();

export const doReportingClosePage = createAction('@doReportingClosePage')<{
  key: string;
}>();

export const doReportingShowReportFilterModal = createAction(
  '@doReportingShowReportFilterModal',
)<{
  key: string;
}>();

export const doReportingSetReports = createAction('@doReportingSetReports')<{
  reports: {
    id: string;
    name: string;
    type: ReportEnum;
    typeName: string;
    typeIsDefault: boolean;
    description: string;
  }[];
}>();

export const doReportingSetReport = createAction('@doReportingSetReport')<{
  key: string;
  id: string;
  name: string;
  description: string;
  type: ReportEnum;
  typeName: string;
  typeIsDefault: boolean;
  filterOptions: ReportData['filterOptions'];
  filterValues: ReportData['filterValues'];
}>();

export const doReportingResetDraftFilterValues = createAction(
  '@doReportingResetDraftFilterValues',
)<{
  key: string;
}>();

export const doReportingMergeDraftFilterValues = createAction(
  '@doReportingMergeDraftFilterValues',
)<
  {
    key: string;
    filterValues: Partial<ReportData['filterValues']>;
  } & (
    | {
        loadData: false;
        closeModals?: undefined;
        overlayLoader?: undefined;
      }
    | {
        loadData: true;
        closeModals?: true;
        overlayLoader?: true;
      }
  )
>();

export const doReportingSetFilterValues = createAction(
  '@doReportingSetFilterValues',
)<{
  key: string;
  filterValues: ReportData['filterValues'];
}>();

export const doReportingGetReportListData = createAction(
  '@doReportingGetReportListData',
)<{
  key: string;
  /**
   * false to clear existing data and load page 1
   * true to load the next page
   */
  nextPage: boolean;
  closeModals: boolean;
  overlayLoader: boolean;
}>();

export const doReportingSetReportVisualsSize = createAction(
  '@doReportingSetReportVisualsSize',
)<{ size: 'large' | 'small' | 'none' }>();

export const doReportingGetReportCsv = createAction(
  '@doReportingGetReportCsv',
)<{ key: string }>();

export const doReportingSetDataListLoading = createAction(
  '@doReportingSetDataListLoading',
)<{
  key: string;
  loading: boolean;
}>();

export const doReportingMergeDataListRaw = createAction(
  '@doReportingMergeDataListRaw',
)<{
  key: string;
  merge: boolean;
  pageNumber: number;
  response: ReportTypes[ReportEnum]['apiResponse'];
}>();

export const doReportingSetDataVisualRaw = createAction(
  '@doReportingSetDataVisualRaw',
)<{
  key: string;
  response: ReportTypes[ReportEnum]['apiVisualResponse'];
}>();

/**
 * Engage
 */

export const doSetupEngageOpen = createAction('@doSetupEngageOpen')<{
  organisationId: UID;
}>();

export const doSetupEngageClose = createAction('@doSetupEngageClose')();

export const doSetupEngageSetData = createAction('@doSetupEngageSetData')<{
  addToUsage: boolean;
  engage: Engage;
}>();

export const doSetupEngageUpdateGeneral = createAction(
  '@doSetupEngageUpdateGeneral',
)<{
  data: Partial<
    Pick<
      Engage,
      | 'enable'
      | 'enableAsset'
      | 'showAccountablePeople'
      | 'showCompetentPeople'
      | 'showEvacuationStrategy'
      | 'showResponsiblePeople'
      | 'replyToEmail'
      | 'enableReportIssue'
      | 'reportIssueTitle'
      | 'reportIssueDescription'
      | 'reportIssueWebsite'
      | 'reportIssueEmail'
      | 'reportIssuePhone'
      | 'enableTasks'
      | 'enableTaskCategory'
      | 'enableTaskDueDate'
      | 'enableTaskPhotos'
      | 'enableTaskSeverity'
      | 'enableTaskTimescale'
    >
  >;
}>();
export const doSetupEngageAddDocuments = createAction(
  '@doSetupEngageAddDocuments',
)();
export const doSetupEngageOrderDocuments = createAction(
  '@doSetupEngageOrderDocuments',
)();
export const doSetupEngageRemoveDocument = createAction(
  '@doSetupEngageRemoveDocument',
)<{ documentId: string }>();
export const doSetupEngageDocumentUpdate = createAction(
  '@doSetupEngageDocumentUpdate',
)<{ document: EngageDocument }>();

export const doSetupEngageAddElements = createAction(
  '@doSetupEngageAddElements',
)();
export const doSetupEngageOrderElements = createAction(
  '@doSetupEngageOrderElements',
)();
export const eventSetupEngageCheckUpdated = createAction(
  '@eventSetupEngageCheckUpdated',
)<{ eventKey: string; check: EngageElementCheck }>();
export const doSetupEngageRemoveElement = createAction(
  '@doSetupEngageRemoveElement',
)<{ elementId: string }>();
export const doSetupEngageElementUpdate = createAction(
  '@doSetupEngageElementUpdate',
)<{ element: EngageElement }>();

/**
 * API
 */

export const doSetupApiOpen = createAction('@doSetupApiOpen')<{
  organisationId: UID;
}>();

export const doSetupApiClose = createAction('@doSetupApiClose')();

export const doSetupApiSetData = createAction('@doSetupApiSetData')<{
  addToUsage: boolean;
  users: ApiUser[];
  webhooks: ApiWebhook[];
}>();

// API User

export const doSetupApiCreateUserPreflight = createAction(
  '@doSetupApiCreateUserPreflight',
)<{
  organisationId: UID;
}>();

export const doSetupApiCreateUser = createAction('@doSetupApiCreateUser')<{
  organisationId: UID;
  name: string;
  token: string;
}>();

export const doSetupApiUpdateUser = createAction('@doSetupApiUpdateUser')<{
  id: string;
  organisationId: UID;
  name: string;
}>();

export const doSetupApiDeleteUser = createAction('@doSetupApiDeleteUser')<{
  id: string;
  organisationId: UID;
  name: string;
}>();

// API Webhook

export const doSetupApiCreateWebhookPreflight = createAction(
  '@doSetupApiCreateWebhookPreflight',
)<{
  organisationId: UID;
}>();

export const doSetupApiCreateWebhook = createAction(
  '@doSetupApiCreateWebhook',
)<{
  organisationId: UID;
  url: string;
  secret: string;
}>();

export const doSetupApiUpdateWebhook = createAction(
  '@doSetupApiUpdateWebhook',
)<{
  id: string;
  organisationId: UID;
  url: string;
  secret: string | null;
}>();

export const doSetupApiTestWebhook = createAction('@doSetupApiTestWebhook')<{
  id: string | null;
  organisationId: UID;
  url: string;
  secret: string | null;
}>();

export const doSetupApiDeleteWebhook = createAction(
  '@doSetupApiDeleteWebhook',
)<{
  id: string;
  organisationId: UID;
  url: string;
}>();

/**
 * SSO
 */

export const doSetupSsoOpen = createAction('@doSetupSsoOpen')<{
  organisationId: UID;
}>();

export const doSetupSsoSetData = createAction('@doSetupSsoSetData')<{
  addToUsage: boolean;
  data: SsoOrganisation;
}>();

export const doSetupSsoUpdate = createAction('@doSetupSsoUpdate')<
  {
    organisationId: UID;
  } & Partial<{
    samlEnabled: SsoOrganisation['samlEnabled'];
    samlIdpEntityId: SsoOrganisation['samlIdpEntityId'];
    samlIdpCertificate: SsoOrganisation['samlIdpCertificate'];
    scimEnabled: SsoOrganisation['scimEnabled'];
  }>
>();

export const doSetupSsoClose = createAction('@doSetupSsoClose')();

/**
 * Organisation
 */

export const doSetupOrganisationOpen = createAction(
  '@doSetupOrganisationOpen',
)<{ newOrganisationId: UID | null }>();
export const doSetupOrganisationSetData = createAction(
  '@doSetupOrganisationSetData',
)<{
  addToUsage: boolean;
  setupOrganisation: SetupOrganisation;
  customers: Record<string, OrganisationBasic>;
}>();
export const doSetupOrganisationUpdateData = createAction(
  '@doSetupOrganisationUpdateData',
)<{
  setupOrganisation: SetupOrganisation;
}>();
export const doSetupOrganisationClose = createAction(
  '@doSetupOrganisationClose',
)();
export const doSetupOrganisationOnBehalfOfOpen = createAction(
  '@doSetupOrganisationOnBehalfOfOpen',
)();
export const doSetupOrganisationSave = createAction(
  '@doSetupOrganisationSave',
)<{
  changes: EPOrganisationSettingsSave['post'];
}>();

export const doSetupBillingAdminOrganisationSave = createAction(
  '@doSetupBillingAdminOrganisationSave',
)<{
  changes: EPBillingAdminOrganisationSave['post'];
}>();

export const doSetupOrganisationSaveLogo = createAction(
  '@doSetupOrganisationSaveLogo',
)<{
  file: FileBeingAdded | null;
}>();
